/**
 * Detail view for a catalog course. Shows the syllabus and allows ENROLLING
 * (local-first progress).
 *
 * Data source with TWO levels (LF-B):
 *  1. Guest-first REST (`/api/catalog/course/:id`) — PUBLIC courses, also
 *     for guests without sync.
 *  2. Fallback to SYNCED data: a `visibility='org'` course is NOT in the
 *     public catalog (REST 404), but its rows (course/unit/lesson) DO come
 *     down via sync to org members — without this fallback a member saw
 *     "Course not found" on a course they actually have access to.
 *     The same fallback covers the OFFLINE detail of already-synced courses.
 *
 * If no source resolves, `createEntityResolution` distinguishes loading /
 * not-found / offline (no eternal spinners nor lying 404s).
 */
import { createSafeQuery } from "@/lib/api";
import { type Component, createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { BookOpen, CheckCircle2, PlayCircle, Star, Users } from "@fleet/icons";
import {
  courseQueryKeys,
  type CourseTreeDTO,
  enrollMutation,
  fetchCatalogCourse,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link, useNavigate } from "@tanstack/solid-router";
import { SERVER_URL } from "@/lib/runtime-config";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@fleet/ui";
import { NotFoundState } from "@/components/ui/not-found-state";
import { showToast } from "@/components/ui/toast";
import { createEntityResolution } from "@/lib/use-entity-resolution";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useCourseTree, useCourseWrite, useEnrollment } from "../../hooks/use-course-data";
import { CourseCohortEnroll } from "@/features/academy/components/course-cohort-enroll";

/** Normalized view model (public REST or synced rows). */
interface CourseView {
  title: string;
  description: string | null;
  lessonCount: number;
  /** Only the public REST knows this; hidden in sync fallback. */
  enrolledCount: number | null;
  units: {
    id: string;
    title: string;
    lessons: { id: string; title: string; blockCount: number | null; xp: number }[];
  }[];
}

export const CourseDetailView: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { db: syncDb } = usePowerSyncContext();
  const { drizzle, userId } = useCourseWrite();
  const enrollment = useEnrollment(() => props.courseId);
  const isEnrolled = () => Boolean(enrollment.data && !enrollment.data.deleted_at);

  const tree = createSafeQuery<CourseTreeDTO>(() => ({
    queryKey: courseQueryKeys.catalogCourse(props.courseId),
    queryFn: ({ signal }) => fetchCatalogCourse(SERVER_URL, props.courseId, { signal }),
    staleTime: 30_000,
    // A 404 for an org-visible course isn't fixed by retrying; the synced
    // fallback answers instantly.
    retry: 1,
  }));

  // Local-first fallback: the course may be synced (member of the course's
  // org, or course already downloaded) even if the public catalog denies it.
  const local = useCourseTree(() => props.courseId);

  // The REST "decided": has data, failed, or stayed PAUSED offline (networkMode
  // offlineFirst leaves the query pending+paused with no network — without this
  // case the offline detail stayed in an eternal skeleton instead of falling
  // back).
  const restSettled = () => !tree.isPending || tree.fetchStatus === "paused";

  const view = createMemo<CourseView | null>(() => {
    const dto = tree.data;
    if (dto) {
      return {
        title: dto.course.title,
        description: dto.course.description ?? null,
        lessonCount: dto.units.reduce((n, u) => n + u.lessons.length, 0),
        enrolledCount: dto.enrolledCount,
        units: dto.units.map((u) => ({
          id: u.unit.id,
          title: u.unit.title,
          lessons: u.lessons.map((l) => ({
            id: l.lesson.id,
            title: l.lesson.title,
            blockCount: l.blocks.length,
            xp: l.lesson.xpReward,
          })),
        })),
      };
    }
    // The REST is still in flight → we haven't decided yet (avoids the fallback flash).
    if (!restSettled()) {
      return null;
    }
    const course = local.course.data;
    if (!course) {
      return null;
    }
    const lessons = local.lessons.data ?? [];
    return {
      title: course.title ?? "",
      description: course.description ?? null,
      lessonCount: lessons.length,
      enrolledCount: null,
      units: (local.units.data ?? []).map((u) => ({
        id: u.id,
        title: u.title ?? "",
        lessons: lessons
          .filter((l) => l.unit_id === u.id)
          .map((l) => ({
            id: l.id,
            title: l.title ?? "",
            blockCount: null,
            xp: l.xp_reward ?? 0,
          })),
      })),
    };
  });

  /** The detail comes from the synced fallback (not the public catalog). */
  const isSyncFallback = () => !tree.data && Boolean(local.course.data);

  // No REST and no local row: loading, doesn't exist, or simply offline and
  // not yet synced? (canonical resolution over sync state).
  const resolution = createEntityResolution({
    db: syncDb,
    hasEntity: () => Boolean(view()),
    isPending: () => !restSettled() || local.course.isLoading,
  });

  const enroll = async () => {
    const db = drizzle();
    const uid = userId();
    if (!db || !uid) {
      return;
    }
    await enrollMutation(db, uid, props.courseId);
    showToast(t(msg`Enrolled — your progress is saved on this device`), "success");
    void navigate({ to: "/learn/$courseId", params: { courseId: props.courseId } });
  };

  return (
    <div class="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
      <Link
        to="/catalog"
        class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-accent"
      >
        ← {t(msg`Catalog`)}
      </Link>
      <Show
        when={view()}
        fallback={
          <Show
            when={resolution() === "loading"}
            fallback={
              <div data-testid="course-not-found">
                <NotFoundState
                  variant={resolution() === "offline" ? "offline" : "not-found"}
                  title={t(msg`Course not available`)}
                  description={
                    resolution() === "offline"
                      ? t(
                          msg`You're offline and this course hasn't synced to this device yet. Reconnect and try again.`,
                        )
                      : t(msg`This course doesn't exist or is not available to you.`)
                  }
                  primaryAction={{
                    label: t(msg`Back to catalog`),
                    onClick: () => void navigate({ to: "/catalog" }),
                  }}
                />
              </div>
            }
          >
            <Skeleton class="h-44 w-full rounded-3xl" />
          </Show>
        }
      >
        {(data) => (
          <>
            <div class="space-mesh rounded-3xl border border-border p-6">
              <div class="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-accent-foreground shadow-md shadow-accent/25">
                <BookOpen class="h-7 w-7" />
              </div>
              <h1
                class="font-display text-2xl font-extrabold tracking-tight md:text-3xl"
                data-testid="course-title"
              >
                {data().title}
              </h1>
              <Show when={data().description}>
                <p class="mt-2 text-muted-foreground">{data().description}</p>
              </Show>
              <div class="mt-4 flex flex-wrap items-center gap-2">
                <span class="inline-flex items-center gap-1.5 rounded-full bg-accent/12 px-3 py-1 text-xs font-semibold text-accent">
                  <BookOpen class="h-3.5 w-3.5" />{" "}
                  {t(msg`{count} lessons`, { count: data().lessonCount })}
                </span>
                <Show when={data().enrolledCount != null}>
                  <span class="inline-flex items-center gap-1.5 rounded-full bg-joy-sky/15 px-3 py-1 text-xs font-semibold text-joy-sky">
                    <Users class="h-3.5 w-3.5" />{" "}
                    {t(msg`{count} enrolled`, { count: data().enrolledCount })}
                  </span>
                </Show>
                {/* Private academy course, served from sync. */}
                <Show when={isSyncFallback()}>
                  <span
                    class="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground"
                    data-testid="course-org-fallback"
                  >
                    {t(msg`Academy course`)}
                  </span>
                </Show>
              </div>
              <div class="mt-5">
                <Show
                  when={!isEnrolled()}
                  fallback={
                    <Button
                      data-testid="course-continue"
                      onClick={() =>
                        navigate({ to: "/learn/$courseId", params: { courseId: props.courseId } })
                      }
                      class="gap-2 rounded-2xl px-6 font-bold shadow-md shadow-accent/25"
                    >
                      <PlayCircle class="h-4 w-4" /> {t(msg`Continue`)}
                    </Button>
                  }
                >
                  <Button
                    data-testid="course-enroll"
                    onClick={enroll}
                    // LF-B: without the local DB ready, the click was a SILENT
                    // no-op (guard `if (!db) return`) — disabled until the
                    // local write-path is available.
                    disabled={!drizzle() || !userId()}
                    class="gap-2 rounded-2xl px-6 font-bold shadow-md shadow-accent/25"
                  >
                    <CheckCircle2 class="h-4 w-4" /> {t(msg`Enroll`)}
                  </Button>
                </Show>
              </div>
            </div>

            {/* F2-A: open cohorts from my academies for this course —
                selection + enrollment request (server-authoritative). */}
            <CourseCohortEnroll courseId={props.courseId} />

            {/* Syllabus */}
            <div class="space-y-4" data-testid="course-syllabus">
              <For each={data().units}>
                {(u) => (
                  <div class="rounded-2xl border border-border bg-surface p-4 shadow-sm">
                    <h3 class="font-display font-bold">{u.title}</h3>
                    <ul class="mt-2 space-y-1">
                      <For each={u.lessons}>
                        {(l) => (
                          <li class="flex items-center justify-between rounded-xl px-2 py-2 text-sm transition-colors hover:bg-accent/5">
                            <span class="font-medium">{l.title}</span>
                            <span class="flex items-center gap-2 text-xs text-muted-foreground">
                              <Show when={l.blockCount != null}>
                                <span>{t(msg`{count} blocks`, { count: l.blockCount })}</span>
                              </Show>
                              <span class="inline-flex items-center gap-1 font-bold text-foreground">
                                <Star class="h-3 w-3 fill-current text-joy-sun" />
                                {l.xp}
                              </span>
                            </span>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                )}
              </For>
            </div>
          </>
        )}
      </Show>
    </div>
  );
};
